/**
 * §6.6 — every month for every bank account, and what is known about each.
 *
 * WHAT THIS ANSWERS THAT THE FORM ABOVE DOES NOT
 *
 * The Reconcile form answers "reconcile this month". The operator's other
 * question is "which months have I not done" — and that is invisible in a list
 * of what exists. A list of statements shows the ones that were fetched; a list
 * of reconciliations shows the ones that were done. Neither shows the gap, and
 * the gap is the thing to act on. So the rows come from a month SERIES and a
 * month with nothing recorded is still a row.
 *
 * TABS PER ACCOUNT, NOT ONE TABLE WITH AN ACCOUNT COLUMN. The months line up
 * across accounts and the balances do not: reading down one account's year is
 * the task, and interleaving three accounts' Septembers turns that into
 * scanning.
 *
 * THE FIRST VERSION OF THIS TABLE WAS REVIEWED BY LOOKING AT IT, which is how
 * the following were found — none of them visible to a test, because jsdom
 * applies no stylesheet and has no layout:
 *
 *   - the heading and the button each wrapped onto two lines;
 *   - the Statement column printed a 45-character S3 filename on every row,
 *     which drowned every other column;
 *   - "from Mercury" repeated on every row to make a point nobody needed twice;
 *   - "not reconciled" as prose beside a date pill, so the eye could not scan
 *     the column;
 *   - the year repeated in every Month cell of a table already filtered to one
 *     account.
 *
 * `preview/shoot.mjs` renders this component against fixtures and photographs
 * it. Run it after changing anything here.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ApiError,
  api,
  type MercuryFetchResult,
  type ReconcilableAccount,
  type StatementMonth,
} from '../api.js';
import { InfoNote } from './InfoNote.js';
import { useCan } from '../auth/AuthProvider.js';
import { PaperclipIcon } from './PaperclipIcon.js';
import { ReceiptOverlay } from './ReceiptOverlay.js';
import { formatCents } from '../lib/money.js';

/** "August", for a YYYY-MM-DD month end. The year is the filter's job. */
function monthName(monthEnd: string): string {
  return new Date(`${monthEnd}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    timeZone: 'UTC',
  });
}

/**
 * Skip reasons the operator has waved away, by account code.
 *
 * THE CREDIT CARD WILL NEVER BE FETCHABLE — Mercury does not list it — so a
 * banner reporting that every single time is a banner that trains the reader to
 * ignore banners. localStorage rather than the database: this is one viewer
 * saying "yes, I know", not a fact about the business, and it should not follow
 * the books into a backup. Wrapped in try/catch because a private window throws
 * on access, and a preview that cannot read it must still render.
 */
const HIDDEN_KEY = 'reconcile.hiddenSkips';

function readHidden(): string[] {
  try {
    const raw = window.localStorage.getItem(HIDDEN_KEY);
    return raw === null ? [] : (JSON.parse(raw) as string[]);
  } catch {
    return [];
  }
}

function writeHidden(codes: string[]): void {
  try {
    window.localStorage.setItem(HIDDEN_KEY, JSON.stringify(codes));
  } catch {
    // A viewer who cannot store it sees the notice again. That is the right
    // failure: nothing is lost but a convenience.
  }
}

type Reconciled = 'all' | 'yes' | 'no';
type HasStatement = 'all' | 'yes' | 'no';

export function StatementMonths({
  accounts,
  accountId,
  onAccountChange,
  refreshKey,
  onDraft,
}: {
  accounts: ReconcilableAccount[];
  /**
   * THE ACCOUNT IS THE PAGE'S, NOT THIS TABLE'S.
   *
   * These tabs used to be a second account picker, sitting a few centimetres
   * below the screen's own `Account` select and disagreeing with it freely: the
   * history above could be showing 1010 while the table below showed 1000, and
   * nothing said so. One selection, owned by the page, and the tabs set it.
   */
  accountId: string;
  onAccountChange: (accountId: string) => void;
  /**
   * Changes when something outside this component has written a reconciliation.
   *
   * A TABLE THAT DOES NOT NOTICE IS WORSE THAN A STALE ONE. Reconciling October
   * left this table showing October unreconciled, beside a History row saying it
   * was reconciled -- reported 2026-09-18. The operator's next move is to
   * reconcile it again, which the database refuses, in a message about a
   * statement date being reconciled once.
   */
  refreshKey?: number;
  /**
   * Fills the Reconcile form above from a row, with the bank's own figure.
   *
   * A DRAFT, NOT A RECONCILIATION, and the distinction is §6.5's. What this
   * removes is the TRANSCRIPTION — opening a PDF, finding the closing balance,
   * retyping it into a form on the same screen — not the comparison. The form
   * is filled in; the operator still reads it and posts it. A reconciliation
   * that fills itself in and confirms itself is one nobody reads.
   */
  onDraft?: (input: {
    /*
     * THE ACCOUNT, because this table has its own tab. The form above keeps a
     * separate account selection, and a row clicked under one account must not
     * draft a balance against another.
     */
    accountId: string;
    monthEnd: string;
    /**
     * Null where the bank's figure is unknown. The drawer is where a statement
     * gets uploaded and a balance typed, so a month with neither is still worth
     * opening -- which is why this is offered on every unreconciled row rather
     * than only the ones that can be pre-filled.
     */
    statementBalanceCents: number | null;
  }) => void;
}) {
  // Fetching writes statements, which is reconcile's (POST /reconciliations/statements/fetch).
  const mayFetch = useCan()('reconcile');
  const [months, setMonths] = useState<StatementMonth[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);
  const [result, setResult] = useState<MercuryFetchResult | null>(null);
  const [hidden, setHidden] = useState<string[]>(readHidden);
  const [viewing, setViewing] = useState<StatementMonth['statement'] | null>(null);

  const [year, setYear] = useState('all');
  const [reconciled, setReconciled] = useState<Reconciled>('all');
  const [hasStatement, setHasStatement] = useState<HasStatement>('all');

  const account = accounts.find((candidate) => candidate.accountId === accountId);
  const sign = account?.normalBalance === 'credit' ? -1 : 1;

  const load = useCallback(() => {
    if (accountId === '') return;
    setMonths(null);
    api
      .listStatementMonths(accountId)
      .then((response) => {
        setMonths(response.months);
        setError(null);
      })
      .catch((caught: unknown) =>
        setError(caught instanceof ApiError ? caught.message : 'Could not read the months.'),
      );
  }, [accountId]);

  /*
   * `refreshKey` is in the dependency list and not used in the body, which
   * looks like a mistake and is the point: it is how a write elsewhere on the
   * screen tells this table to read again.
   */
  useEffect(load, [load, refreshKey]);

  const years = useMemo(
    () => [...new Set((months ?? []).map((row) => row.monthEnd.slice(0, 4)))],
    [months],
  );

  const rows = useMemo(
    () =>
      (months ?? []).filter((row) => {
        if (year !== 'all' && row.monthEnd.slice(0, 4) !== year) return false;
        if (reconciled === 'yes' && row.reconciliation === null) return false;
        if (reconciled === 'no' && row.reconciliation !== null) return false;
        if (hasStatement === 'yes' && row.statement === null) return false;
        if (hasStatement === 'no' && row.statement !== null) return false;
        return true;
      }),
    [months, year, reconciled, hasStatement],
  );

  /**
   * Fetches for EVERY account, not just the tab being looked at.
   *
   * The operator presses this once a month and wants the whole job done. Asking
   * per tab would mean three presses and a way to forget one, and the fetch is
   * idempotent, so the accounts already held cost nothing.
   */
  async function fetchFromMercury() {
    setFetching(true);
    setError(null);
    try {
      setResult(await api.fetchMercuryStatements());
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not fetch statements from Mercury.',
      );
    } finally {
      setFetching(false);
    }
  }

  function hide(code: string) {
    const next = [...new Set([...hidden, code])];
    setHidden(next);
    writeHidden(next);
  }

  const shownSkips = (result?.skipped ?? []).filter((row) => !hidden.includes(row.accountCode));

  if (accounts.length === 0) return null;

  return (
    <section className="statement-months">
      <header className="row statement-months-head">
        {/* `nowrap`: "Statements And Reconciliations" broke over two lines. */}
        <h2>Statements And Reconciliations</h2>
        <InfoNote label="What this table shows">
          Every month since this account first had activity. A blank row is a month with nothing
          recorded against it — which is the row worth looking at, because a gap is invisible in a
          list of what exists.
        </InfoNote>
        <span className="statement-months-spacer" />
        {mayFetch && (
          <button type="button" onClick={() => void fetchFromMercury()} disabled={fetching}>
            {fetching ? 'Fetching…' : 'Fetch From Mercury'}
          </button>
        )}
      </header>

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {result !== null && (
        <div className="notice statement-fetch-result" role="status">
          <div className="row">
            <span>
              {/*
                THREE OUTCOMES, NOT TWO. A run that downloads nothing but fills
                in the closing balance of statements already held is not
                "nothing new" -- it is the run that makes every past month
                comparable, and it happens exactly once. Saying nothing
                happened would be wrong on the one press that matters most.
              */}
              {result.fetched === 0 && result.alreadyHeld > 0
                ? result.balancesRecorded > 0
                  ? `No new statements, and the closing balance recorded for ${result.balancesRecorded} already here.`
                  : `Nothing new — all ${result.alreadyHeld} statements were already here.`
                : `${result.fetched} fetched, ${result.alreadyHeld} already here.`}
            </span>
            {/* Dismissible. It reports a finished action, not a standing condition. */}
            <button
              type="button"
              className="link"
              aria-label="Dismiss"
              onClick={() => setResult(null)}
            >
              ×
            </button>
          </div>

          {shownSkips.length > 0 && (
            <ul className="missed-list">
              {shownSkips.map((row) => (
                <li key={row.accountId}>
                  <strong>{row.accountCode}</strong> — {row.reason}{' '}
                  {/*
                    A permanent no. The credit card is not on Mercury and never
                    will be, so repeating it monthly teaches the operator to
                    stop reading this list — which is where a real new problem
                    would appear.
                  */}
                  <button type="button" className="link" onClick={() => hide(row.accountCode)}>
                    Stop telling me
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <nav className="row tabs" aria-label="Statements by account">
        {accounts.map((candidate) => (
          <button
            key={candidate.accountId}
            type="button"
            className={`tab${candidate.accountId === accountId ? ' is-current' : ''}`}
            aria-current={candidate.accountId === accountId ? 'page' : undefined}
            onClick={() => {
              onAccountChange(candidate.accountId);
              setError(null);
            }}
          >
            {candidate.code} {candidate.name}
          </button>
        ))}
      </nav>

      <div className="row statement-filters">
        <label>
          Year{' '}
          <select aria-label="Year" value={year} onChange={(event) => setYear(event.target.value)}>
            <option value="all">All</option>
            {years.map((candidate) => (
              <option key={candidate} value={candidate}>
                {candidate}
              </option>
            ))}
          </select>
        </label>
        <label>
          Reconciled{' '}
          <select
            aria-label="Reconciled"
            value={reconciled}
            onChange={(event) => setReconciled(event.target.value as Reconciled)}
          >
            <option value="all">All</option>
            <option value="yes">Reconciled</option>
            <option value="no">Not reconciled</option>
          </select>
        </label>
        {/*
          "Statement", matching its column. It was "PDF" while the reconcile
          form sat on this same screen with a `Statement Ending Balance` field
          beside it -- two accessible names starting with the same word for
          different jobs. The form is in a drawer now, and the two are never on
          screen together; `getByLabelText` matches exactly, so neither test nor
          screen reader confuses them.
        */}
        <label>
          Statement{' '}
          <select
            aria-label="Statement"
            value={hasStatement}
            onChange={(event) => setHasStatement(event.target.value as HasStatement)}
          >
            <option value="all">All</option>
            <option value="yes">Has a statement</option>
            <option value="no">Missing</option>
          </select>
        </label>
        {rows.length !== (months?.length ?? 0) && (
          <span className="muted">
            {rows.length} of {months?.length ?? 0} months
          </span>
        )}
      </div>

      {months === null ? (
        !error && <p className="muted">Loading…</p>
      ) : months.length === 0 ? (
        <p className="muted">
          No posted entries on this account yet, so there are no months to show. A month appears
          here once something has been categorized and posted to it.
        </p>
      ) : rows.length === 0 ? (
        <p className="muted">No months match those filters.</p>
      ) : (
        <div className="table-scroll">
          <table className="lines statement-months-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Year</th>
                <th className="statement-cell">Statement</th>
                <th className="statement-cell">Reconciled</th>
                <th className="num">
                  Uncategorized{' '}
                  {/*
                    ANCHORED, because this is a table header. Inline, the body
                    widened the column and inherited the header's uppercase
                    monospace -- two paragraphs of it, over the other headings.
                  */}
                  <InfoNote label="What Uncategorized counts" anchored>
                    Mercury transactions dated in this month that nobody has categorized yet. They
                    are in the bank and not in the books, so they are usually what a variance is
                    made of — categorizing them on the Triage screen is what closes it.
                    <br />
                    <br />
                    This is the month's own count. The variance beside it is cumulative, so a month
                    can show none here and still be out, because of an earlier month.
                  </InfoNote>
                </th>
                <th className="num">Movement</th>
                <th className="num">Book Balance</th>
                <th className="num">Statement Balance</th>
                <th className="num">Variance</th>
                <th className="statement-action">
                  {/* Named for a screen reader, which otherwise announces an
                      empty column header and gives the cell no context. */}
                  <span className="visually-hidden">Draft A Reconciliation</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((month) => {
                /*
                 * STATEMENT MINUS BOOKS, WHICH IS THE API'S DEFINITION.
                 *
                 * This was `books - statement` for a day, and the reconcile
                 * drawer shows `previewReconciliation`'s `differenceCents`,
                 * which is `statement - books`. Same disagreement, opposite
                 * signs, two words for it -- so a row reading +$102,754.40
                 * opened a panel reading -$102,754.40 about the same month.
                 * CLAUDE.md's rule is that a derived quantity several callers
                 * need lives in ONE place; this is the display half of that.
                 * Positive means the bank holds more than the books do.
                 *
                 * THE VARIANCE THAT MATTERS IS ON THE MONTHS NOBODY HAS DONE,
                 * and the first version of this column had it exactly backwards.
                 *
                 * It read `reconciliation.bookBalanceCents -
                 * reconciliation.statementBalanceCents`, which migration 0028's
                 * insert trigger guarantees is ZERO: it recomputes the book
                 * balance and refuses the row unless it equals the statement,
                 * overwriting `book_balance_cents` with what it computed. So the
                 * column printed 0.00 on every reconciled month — a number that
                 * could not be anything else — and a dash on every month where a
                 * difference was possible. `is-variance` could never apply.
                 *
                 * With the closing balance fetched (migration 0040) the useful
                 * comparison exists BEFORE the reconciliation: these are the
                 * books, that is what Mercury says, and the difference is what
                 * the operator will have to explain. Reconciled months still
                 * read 0.00, now because the figures genuinely agree.
                 *
                 * NULL where the bank's figure is unknown — an uploaded PDF
                 * nothing has read. A zero there would say "these agree" about a
                 * comparison never made.
                 */
                const variance =
                  month.statementBalanceCents === null
                    ? null
                    : month.statementBalanceCents - month.bookBalanceCents;

                return (
                  <tr key={month.monthEnd}>
                    <td>{monthName(month.monthEnd)}</td>
                    <td className="muted">{month.monthEnd.slice(0, 4)}</td>
                    <td className="statement-cell">
                      {month.statement === null ? (
                        <span className="muted" aria-label="No statement">
                          —
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="icon-button"
                          title={`${month.statement.filename} (${
                            month.statement.mercuryStatementId === null
                              ? 'uploaded'
                              : 'fetched from Mercury'
                          })`}
                          aria-label={`Open ${month.statement.filename}`}
                          onClick={() => setViewing(month.statement)}
                        >
                          <PaperclipIcon />
                        </button>
                      )}
                    </td>
                    <td className="statement-cell">
                      {/*
                        A TICK OR NOTHING. It was a tick or a cross, and a column
                        of crosses down every unreconciled month read as a screen
                        full of errors -- when an unreconciled month is ordinary
                        work, not a fault. Absence says "not yet" perfectly well,
                        and it leaves the ticks to be the thing the eye finds.

                        The empty cell is deliberately not `aria-label`ed: a
                        screen reader announcing "not reconciled" on every row
                        would reintroduce exactly the noise the crosses were.
                      */}
                      {month.reconciliation !== null && (
                        <span
                          className="mark is-yes"
                          title={`Reconciled ${month.reconciliation.createdAt.slice(0, 10)}`}
                          aria-label={`Reconciled ${month.reconciliation.createdAt.slice(0, 10)}`}
                        >
                          ✓
                        </span>
                      )}
                    </td>
                    <td className={`num${month.uncategorizedCount > 0 ? ' is-waiting' : ''}`}>
                      {/*
                        A DASH, NOT A ZERO, when there is nothing waiting. Zero
                        is a number the eye stops at; this column is scanned for
                        the months that have work in them, and a column of 0s
                        makes the two that matter harder to find.
                      */}
                      {month.uncategorizedCount === 0 ? (
                        <span className="muted">—</span>
                      ) : (
                        month.uncategorizedCount
                      )}
                    </td>
                    <td className="num">{formatCents(month.bookMovementCents * sign)}</td>
                    <td className="num">{formatCents(month.bookBalanceCents * sign)}</td>
                    <td className="num">
                      {month.statementBalanceCents === null ? (
                        <span className="muted">—</span>
                      ) : (
                        /*
                          Reported and confirmed are different claims, and the
                          figure alone cannot say which it is. A second column
                          saying "from Mercury" on every row is the thing the
                          operator asked to have removed once already, so the
                          title carries it.
                        */
                        <span
                          className={
                            month.statementBalanceSource === 'statement' ? 'reported-balance' : undefined
                          }
                          title={
                            month.statementBalanceSource === 'reconciliation'
                              ? 'Confirmed when this month was reconciled'
                              : 'Reported by Mercury on the statement'
                          }
                        >
                          {formatCents(month.statementBalanceCents * sign)}
                        </span>
                      )}
                    </td>
                    <td className={`num${variance !== null && variance !== 0 ? ' is-variance' : ''}`}>
                      {variance === null ? (
                        <span className="muted">—</span>
                      ) : (
                        formatCents(variance * sign)
                      )}
                    </td>
                    <td className="statement-action">
                      {/*
                        EVERY UNRECONCILED MONTH, not only the ones that can be
                        pre-filled. It was restricted to rows with a fetched
                        balance while the form sat inline, where opening it with
                        a blank achieved nothing; the drawer is where a statement
                        is uploaded and a balance typed, so the months with
                        NEITHER are the ones most worth being able to open.

                        Still absent on a reconciled month: it would invite
                        overwriting a check somebody made, and 0028 refuses a
                        second live reconciliation for the date anyway, so it
                        would be a button that always errors.
                      */}
                      {onDraft !== undefined && month.reconciliation === null && (
                        <button
                          type="button"
                          className="link"
                          onClick={() =>
                            onDraft({
                              accountId,
                              monthEnd: month.monthEnd,
                              statementBalanceCents: month.statementBalanceCents,
                            })
                          }
                        >
                          Reconcile
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/*
        THE SAME MODAL RECEIPTS USE. Copying it would have copied the three
        gates in its header with it -- the `inline` disposition, the `frame-src`
        policy, and the browser setting that turns a frame into a download --
        and those took a session to find once. `resolveUrl` is the only
        difference: a statement's signed URL comes from its own endpoint.
      */}
      {viewing !== null && (
        <ReceiptOverlay
          label="Statement"
          attachment={
            {
              id: viewing.id,
              originalFilename: viewing.filename,
              contentType: 'application/pdf',
              sizeBytes: viewing.sizeBytes,
            } as never
          }
          resolveUrl={(id, disposition) => api.statementUrl(id, disposition)}
          onClose={() => setViewing(null)}
        />
      )}
    </section>
  );
}
